import { memo } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Button,
  Stack,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react'

import { Amplitude } from 'utils/amplitude.utils'
import { AppointmentModifyEventProps } from 'utils/amplitude.types'
import { useSchedulingContext } from 'context/SchedulingContext/SchedulingProvider'
import ChakraPrimaryButton from 'components/Button/ChakraPrimaryButton'

interface CostVerificationModalProps {
  open: boolean
  handleClose: () => void
  onKeepVisit: () => void
  onCancelAnyways: () => void
}

const CostVerificationModal: React.FC<CostVerificationModalProps> = ({
  open,
  handleClose,
  onKeepVisit,
  onCancelAnyways,
}) => {
  const {
    appointment,
    appointmentType,
    costConcernAppointment,
  } = useSchedulingContext()

  const amplitudeEventProps: AppointmentModifyEventProps = {
    appointment_id: appointment?.id ?? '',
    appointment_type_name: appointmentType?.name ?? '',
    appointment_time: appointment?.start_at ?? '',
  }

  const handleKeepVisit = () => {
    Amplitude.appointmentCancelReasonModalClick({
      ...amplitudeEventProps,
      cta: 'dont_cancel',
    })
    costConcernAppointment()
  }

  const handleCancelAnyways = () => {
    Amplitude.appointmentCancelReasonModalClick({
      ...amplitudeEventProps,
      cta: 'cancel',
    })
    onCancelAnyways()
  }

  return (
    <Modal isOpen={open} onClose={handleClose} isCentered size="xs">
      <ModalOverlay />
      <ModalContent maxW="364px">
        <ModalHeader
          borderBottom="none"
          fontFamily="Instrument Serif"
          fontSize="24px"
          fontWeight="400"
          lineHeight="100%"
          pt="20px"
          px="20px"
          pb="16px"
          color="var(--blue-dark)"
        >
          We will verify the cost of your visit
        </ModalHeader>
        <ModalCloseButton mt="7px" fontWeight="400" color="#9091A4" />
        <ModalBody px="20px" pb="20px" pt="0">
          <Box width="100%" mb="16px">
            <Text
              fontSize="16px"
              fontWeight="regular"
              lineHeight="24px"
              fontFamily="Inter"
              color="#1B294D"
              mb="16px"
            >
              We understand your cost concerns, but we don&apos;t want that to
              stop you from getting care. Our billing team will review your
              insurance plan and send you the expected visit cost within 2 days.
            </Text>
          </Box>
          <Stack spacing={3} width="100%">
            <ChakraPrimaryButton onClick={handleKeepVisit} variant="dark-blue">
              Keep visit and wait for cost info
            </ChakraPrimaryButton>
            <Button
              variant="outline"
              width="100%"
              height="36px"
              borderRadius="8px"
              borderColor="#141743"
              color="#1F2937"
              onClick={handleCancelAnyways}
              fontSize="14px"
              fontWeight="600"
              _hover={{ bg: 'transparent', borderColor: '#9CA3AF' }}
            >
              Cancel anyways
            </Button>
          </Stack>
          <Box
            mt="17px"
            borderTop="1px solid #E5E7EB"
            borderBottom="1px solid #E5E7EB"
          >
            <Accordion allowToggle>
              <AccordionItem border="none">
                <AccordionButton
                  px={0}
                  _hover={{ bg: 'transparent' }}
                  _expanded={{ bg: 'transparent' }}
                >
                  <Text
                    fontSize="16px"
                    color="#1F2937"
                    textAlign="left"
                    marginBottom="5px"
                  >
                    What if it turns out I&apos;m not in network based on my
                    plan?
                  </Text>
                  <AccordionIcon color="#6B7280" />
                </AccordionButton>
                <AccordionPanel p={0}>
                  <Text
                    fontSize="16px"
                    fontWeight="regular"
                    lineHeight="24px"
                    fontFamily="Inter"
                    color="#1F2937"
                    mt="0px"
                  >
                    You&apos;ll only pay $120 for your initial medical visit and
                    $75 for your initial visit with a Registered Dietitian.
                    After that, we&apos;ll either bill your insurance or explore
                    self-pay options to continue your care.
                  </Text>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default memo(CostVerificationModal)
